// Spec 48 ai-ux — context_pill.
//
// "Pill indicating the screen or entity supplying ambient context to
// the thread. Dismissable per-thread; dismissing severs context.
// Updates live when user navigates between screens." (spec 48 line 1206)
//
// a11y per spec 48:
//   - Pill uses aria-label='Current context: {subject} — press x or
//     dismiss button to remove'
//   - Dismiss is a button with aria-label='Remove context'
//   - Context change announces via aria-live='polite' on the pill
//   - Dismiss triggers a system bubble 'Context removed' in the thread
//     for audit (consumer's responsibility — this component just emits
//     the onDismiss callback)

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface ContextPillProps extends HTMLAttributes<HTMLDivElement> {
  /** The context subject text, e.g., "Jamie L." or "Weekly Payroll Report". */
  readonly contextSubject: string;
  /** Optional decorative icon matching context kind (person/report/screen). */
  readonly icon?: ReactNode;
  /** Click → consumer severs context + emits a system bubble. */
  readonly onDismiss?: () => void;
  /** i18n: dismiss button accessible label. */
  readonly dismissLabel?: string;
  /** i18n: tooltip on the dismiss control. */
  readonly dismissTooltip?: string;
}

export const ContextPill = forwardRef<HTMLDivElement, ContextPillProps>(
  (
    {
      contextSubject,
      icon,
      onDismiss,
      dismissLabel = "Remove context",
      dismissTooltip = "Remove context",
      className,
      ...rest
    },
    ref,
  ) => {
    const ariaLabel = `Current context: ${contextSubject}${
      onDismiss !== undefined ? " — press dismiss to remove" : ""
    }`;
    return (
      <div
        ref={ref}
        // Spec rule: "Context change announces via aria-live='polite' on
        // the pill" — caller swaps the pill with new contextSubject; the
        // live region picks up the text change.
        aria-live="polite"
        aria-label={ariaLabel}
        data-component="context-pill"
        className={cx("nai-context-pill", className)}
        {...rest}
      >
        {icon !== undefined && (
          <span
            aria-hidden="true"
            data-component="context-pill-icon"
            className="nai-context-pill-icon"
          >
            {icon}
          </span>
        )}
        <span data-component="context-pill-label" className="nai-context-pill-label">
          {`Context: ${contextSubject}`}
        </span>
        {onDismiss !== undefined && (
          <button
            type="button"
            data-component="context-pill-dismiss"
            className="nai-context-pill-dismiss"
            aria-label={dismissLabel}
            title={dismissTooltip}
            onClick={onDismiss}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
    );
  },
);
ContextPill.displayName = "ContextPill";
